import React from "react";
import { IoPlayOutline } from "react-icons/io5";
import { usePublicLang } from "../../../config/i18n/public";
import SteffiDevice from "../../Steffi/components/SteffiDevice";

export default function SteffiRegSection() {
  const { copy } = usePublicLang();
  const deviceAlt = copy.speech_cards.device_alt;

  return (
    <section className="w-full bg-[#101011] text-white overflow-x-clip">
      <div className="mx-auto w-full max-w-[1480px] px-4 pt-20 pb-28 sm:px-6 lg:px-8 md:pt-24 md:pb-36">
        <div className="grid items-start gap-8 md:gap-10 md:grid-cols-4">
          {/* LEFT: device column */}
          <div className="relative flex items-start justify-center md:justify-start min-w-0">
            <div className="md:sticky md:top-28 self-start">
              <SteffiDevice
                variant="green"
                alt={deviceAlt}
                float={false}
                className="
                  w-[210px] xs:w-[250px] sm:w-[300px] md:w-[400px] lg:w-[440px]
                  ml-4 sm:ml-6 md:ml-4
                  drop-shadow-[0_20px_40px_rgba(0,0,0,0.25)]
                  select-none
                "
              />
            </div>
          </div>

          {/* RIGHT: content (sticky on md+) */}
          <div className="md:col-span-3 min-w-0 md:sticky md:top-36 self-start">
            <CTAReplacement />
          </div>
        </div>
      </div>
    </section>
  );
}

function CTAReplacement() {
  const { copy, lang } = usePublicLang();
  const c = copy.sticky;
  const bubbleSources =
    lang === "en"
      ? ["/essen_en.mp3", "/events_en.mp3", "/quiz_en.mp3"]
      : ["/essen_de.mp3", "/events.mp3", "/quiz.mp3"];
  const bubbles = c.bubbles.map((text, i) => ({
    text,
    src: bubbleSources[i] || bubbleSources[0],
  }));
  const highlightClass = (i) =>
    i === 2 ? "text-[#ffcc7a] font-bold" : "text-[#ffc15d] font-bold";
  const renderSteffiText = (text, cls) => {
    const parts = String(text).split("{steffi}");
    if (parts.length === 1) return text;
    return parts.map((part, idx) => (
      <React.Fragment key={idx}>
        {idx > 0 && <span className={cls}>Steffi</span>}
        {part}
      </React.Fragment>
    ));
  };
  const plainSteffiText = (text) => String(text).split("{steffi}").join("Steffi");
  const renderBottom = (text) => {
    const match = String(text).match(/^(.*)“(.+?)”(.*)$/);
    if (!match) return text;
    return (
      <>
        {match[1]}
        <span className="text-[#6FB1FF]">“{match[2]}”</span>
        {match[3]}
      </>
    );
  };

  const audioRef = React.useRef(null);
  const [playingIndex, setPlayingIndex] = React.useState(null);
  const [isPlaying, setIsPlaying] = React.useState(false);

  React.useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setIsPlaying(false);
      setPlayingIndex(null);
    };

    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnded);
    el.addEventListener("error", onEnded);
    return () => {
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnded);
      el.removeEventListener("error", onEnded);
    };
  }, []);

  const handlePlay = async (index) => {
    const el = audioRef.current;
    if (!el) return;

    if (playingIndex === index && !el.paused) {
      el.pause();
      setPlayingIndex(null);
      return;
    }

    try {
      const target = bubbles[index].src;
      const abs = new URL(target, window.location.href).href;
      if (el.src !== abs) {
        el.src = target;
      } else {
        el.currentTime = 0;
      }
      await el.play();
      setPlayingIndex(index);
    } catch (e) {
      console.error("Audio play failed:", e);
    }
  };

  const handleKey = (e, index) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handlePlay(index);
    }
  };

  const togglePlayPause = async () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      try {
        await el.play();
      } catch (e) {
        console.error(e);
      }
    } else {
      el.pause();
    }
  };

  return (
    <div className="flex w-full flex-col items-start sm:items-end text-white min-w-0">
      {/* Headline */}
      <h2 className="w-full text-left sm:text-right text-2xl sm:text-4xl md:text-5xl font-extrabold leading-tight text-white">
        {c.title}
      </h2>

      {/* Controls row */}
      <div className="mt-6 sm:mt-8 flex w-full items-center justify-start sm:justify-end gap-3 sm:gap-4 min-w-0">
        {false &&
        <LanguageDropdown
          onSelect={() => {
            /* hook up later */
          }}
        />}
        <div className="hidden sm:flex items-center ml-2">
          <DotsVisualizer isPlaying={isPlaying} count={18} />
        </div>

        <div className="ml-auto sm:ml-2">
          <button
            onClick={togglePlayPause}
            className="grid h-11 w-11 sm:h-12 sm:w-12 place-items-center rounded-full bg-[#2B2C30] text-white/90 ring-1 ring-white/10 shadow hover:bg-[#34353A] focus:outline-none focus:ring-2 focus:ring-white/20"
            aria-label={isPlaying ? c.aria_pause : c.aria_play}
            aria-pressed={isPlaying}
          >
            {isPlaying ? <PauseIcon /> : <IoPlayOutline />}
          </button>
        </div>
      </div>

      {/* Speech bubbles */}
      <div className="mt-10 sm:mt-14 flex w-full flex-col items-end gap-3 sm:gap-4 min-w-0">
        {bubbles.map((b, i) => {
          const playing = playingIndex === i;
          const plainText = plainSteffiText(b.text);
          const ariaLabel = `${playing ? c.aria_pause : c.aria_play}: ${plainText}`;
          return (
            <Bubble
              key={i}
              tone="dark"
              onClick={() => handlePlay(i)}
              onKeyDown={(e) => handleKey(e, i)}
              playing={playing}
              ariaLabel={ariaLabel}
            >
              {renderSteffiText(b.text, highlightClass(i))}
            </Bubble>
          );
        })}
      </div>

      {/* Bottom strip */}
      <div className="mt-12 sm:mt-16 w-full sm:w-5/6 md:w-3/4 rounded-xl px-4 py-5 sm:py-6 ring-1 ring-white/5 shadow-[0_-6px_30px_-10px_rgba(0,0,0,0.35)]">
        <p className="mx-auto text-center text-lg sm:text-xl md:text-2xl font-semibold text-white/90">
          {renderBottom(c.bottom)}
        </p>
      </div>

      <audio ref={audioRef} preload="metadata" />
    </div>
  );
}

/* Clickable bubble */
function Bubble({ children, tone = "dark", onClick, onKeyDown, playing = false, ariaLabel }) {
  const base =
    "relative max-w-full md:max-w-[42rem] text-[16px] sm:text-[18px] md:text-[20px] font-semibold rounded-full px-4 sm:px-5 py-3 ring-1 flex items-center gap-3 transition-all duration-200 focus:outline-none min-w-0 whitespace-normal break-words hyphens-auto";
  const toneCls =
    tone === "dark"
      ? "bg-[#1F5CB0] text-white ring-white/10"
      : "bg-[#8EB8F9] text-white ring-white/20";
  const stateCls = playing
    ? "shadow-[0_0_0_4px_rgba(255,255,255,0.15)]"
    : "shadow-[0_10px_25px_rgba(0,0,0,0.25)] hover:translate-y-[-1px] active:translate-y-0";

  return (
    <button
      type="button"
      className={`${base} ${toneCls} ${stateCls}`}
      onClick={onClick}
      onKeyDown={onKeyDown}
      aria-pressed={playing}
      aria-label={ariaLabel}
      title={ariaLabel}
    >
      <span className="flex-1 min-w-0">{children}</span>
      <span className={`shrink-0 opacity-90 ${playing ? "animate-pulse" : ""}`} aria-hidden>
        {playing ? <PauseIcon /> : <IoPlayOutline />}
      </span>
    </button>
  );
}

function DotsVisualizer({ isPlaying, count = 18 }) {
  return (
    <>
      <div className="flex items-center gap-2 sm:gap-3">
        {Array.from({ length: count }).map((_, i) => (
          <span
            key={i}
            className="block h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-white"
            style={{
              animation: `dotPulse 1s ease-in-out ${i * 0.08}s infinite`,
              animationPlayState: isPlaying ? "running" : "paused",
              opacity: 0.3,
            }}
            aria-hidden
          />
        ))}
      </div>
      <style>{`
        @keyframes dotPulse {
          0%, 100% { opacity: .25; transform: translateY(0); }
          50%       { opacity: 1;   transform: translateY(-4px); }
        }
      `}</style>
    </>
  );
}

function LanguageDropdown({ onSelect }) {
  const langs = [
    { code: "de", label: "Deutsch" },
    { code: "en", label: "English" },
    { code: "fr", label: "Français" },
    { code: "es", label: "Español" },
    { code: "it", label: "Italiano" },
    { code: "nl", label: "Nederlands" },
    { code: "pl", label: "Polski" },
    { code: "pt", label: "Português" },
    { code: "sv", label: "Svenska" },
    { code: "tr", label: "Türkçe" },
  ];

  const [open, setOpen] = React.useState(false);
  const [selected, setSelected] = React.useState(langs[0]);
  const menuRef = React.useRef(null);

  React.useEffect(() => {
    const onDocClick = (e) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  const handleSelect = (lang) => {
    setSelected(lang);
    setOpen(false);
    onSelect?.(lang);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 rounded-full bg-[#1F1F22] px-3.5 py-2 text-[15px] font-semibold text-white/90 ring-1 ring-white/10 shadow-sm hover:bg-[#232329] focus:outline-none focus:ring-2 focus:ring-white/20"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Sprache wählen"
      >
        {selected.label}
        <ChevronDownIcon />
      </button>

      {open && (
        <div
          role="listbox"
          tabIndex={-1}
          className="absolute right-0 z-30 mt-2 w-52 overflow-hidden rounded-2xl bg-[#1F1F22] ring-1 ring-white/10 shadow-2xl"
        >
          <ul className="max-h-72 overflow-auto py-2">
            {langs.map((lang) => {
              const isActive = lang.code === selected.code;
              return (
                <li key={lang.code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    onClick={() => handleSelect(lang)}
                    className={`flex w-full items-center justify-between px-3 py-2.5 text-sm text-white/90 hover:bg-white/5 focus:bg-white/10 ${
                      isActive ? "bg-white/5" : ""
                    }`}
                  >
                    <span>{lang.label}</span>
                    {isActive && (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="opacity-80"
                      >
                        <path d="M9 16.2l-3.5-3.5 1.4-1.4L9 13.4l7.1-7.1 1.4 1.4z" />
                      </svg>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

/* tiny icons */
function ChevronDownIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M7 10l5 5 5-5H7z" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
    </svg>
  );
}
